import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

interface PracticeClockDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  timer: number;
  isRunning: boolean;
  isPaused: boolean;
  isStarting: boolean;
  totalDistance: number;
  totalSets: number;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onReset: () => void;
  onFinish: () => void;
}

/**
 * Dialog component for practice clock with timer and controls
 */
export function PracticeClockDialog({
  isOpen,
  onOpenChange,
  timer,
  isRunning,
  isPaused,
  isStarting,
  totalDistance,
  totalSets,
  onStart,
  onPause,
  onResume,
  onReset,
  onFinish,
}: PracticeClockDialogProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Practice Clock</DialogTitle>
          <DialogDescription>
            Time and performance synced with your Bluetooth device
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center space-y-6 mt-4">
          {/* Timer */}
          <div className="text-6xl font-bold tabular-nums" style={{ fontFamily: "'Digital-7 Mono', monospace" }}>
            {formatTime(timer)}
          </div>

          {/* Control buttons */}
          <div className="flex gap-4">
            {!isRunning && !isPaused && (
              <Button
                onClick={onStart}
                className="bg-green-600 hover:bg-green-700 text-white px-6"
                disabled={isStarting}
              >
                {isStarting ? "Connecting..." : "Start"}
              </Button>
            )}

            {isRunning && (
              <Button
                onClick={onPause}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-6"
              >
                Pause
              </Button>
            )}

            {!isRunning && isPaused && (
              <Button
                onClick={onResume}
                className="bg-green-600 hover:bg-green-700 text-white px-6"
              >
                Resume
              </Button>
            )}

            <Button onClick={onReset} variant="outline">
              Reset
            </Button>
          </div>

          {/* Laps summary */}
          <div className="flex justify-between w-full mt-4">
            <span className="text-sm text-muted-foreground">
              Laps: {totalSets}
            </span>
            <span className="text-sm text-muted-foreground">
              Distance: {totalDistance} m
            </span>
          </div>

          {/* Finish */}
          <Button
            onClick={onFinish}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            Finish
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

